import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ParallaxComponent.css';
import './../../HomePage/ExpertiseSectionHome/ExpertiseSectionHome.css';
import { ParallaxTextProps } from './../../../types/ParallaxTextTypes' // Assurez-vous que le chemin est correct

interface ParallaxImagesProps {
  layer1: string;
  layer2: string;
  layer3: string;
}

interface ParallaxProps {
  images: ParallaxImagesProps;
  textContent: ParallaxTextProps;
  contentBelowParallax?: React.ReactNode;
  wrapperClassName?: string;
  headerClassName?: string;
  translationNamespace?: string;
}

const ParallaxComponent: React.FC<ParallaxProps> = ({
  images,
  textContent,
  contentBelowParallax,
  wrapperClassName = 'parallax__wrapper',
  headerClassName = 'parallax__header',
  translationNamespace = 'adminCloud',
}) => {
  const { t } = useTranslation();
  const parallaxRef = useRef<HTMLDivElement>(null);
  const layersRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    if (parallaxRef.current && layersRef.current) {
      const layers = [
        { layer: '1', yPercent: 70 },
        { layer: '2', yPercent: 55 },
        { layer: '3', yPercent: 40 },
        { layer: '4', yPercent: 10 },
      ];

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: parallaxRef.current,
          start: '0% 0%',
          end: '100% 0%',
          scrub: 0,
        },
      });

      layers.forEach((layerObj, idx) => {
        tl.to(
          layersRef.current!.querySelectorAll(
            `[data-parallax-layer="${layerObj.layer}"]`
          ),
          {
            yPercent: layerObj.yPercent,
            ease: 'none',
          },
          idx === 0 ? undefined : '<'
        );
      });

      if (sectionRef.current) {
        gsap.to(sectionRef.current, {
          yPercent: -15,
          ease: 'none',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      }
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      gsap.ticker.remove(() => {});
    };
  }, []);

  return (
    <div className={wrapperClassName} ref={parallaxRef}>
      <div ref={sectionRef}>
        <section className={headerClassName}>
          <div className="parallax__visuals">
            <div className="parallax__black-line-overflow"></div>
            <div data-parallax-layers className="parallax__layers" ref={layersRef}>
              <img
                src={images.layer1}
                loading="eager"
                width="800"
                data-parallax-layer="1"
                alt=""
                className="parallax__layer-img"
              />
              <img
                src={images.layer2}
                loading="eager"
                width="800"
                data-parallax-layer="2"
                alt=""
                className="parallax__layer-img"
              />
              <div data-parallax-layer="3" className="parallax__layer-title">
                <div className="expertise-row">
                  <div className="columns column-1 no-overflow">
                    <div className="column column-inner tw-mt-[3em]">
                      <h4 className="h4-white">{t(`${translationNamespace}.parallax.leftTitle`, textContent.leftTitle)}</h4>
                      <p className="grey-text">{t(`${translationNamespace}.parallax.leftText`, textContent.leftText)}</p>
                      {textContent.leftExtraTexts && textContent.leftExtraTexts.length > 0 && (
                        <div className="extra-texts">
                          {textContent.leftExtraTexts.slice(0, 3).map((text, index) => (
                            <p key={index} className="grey-text extra-text">
                              {t(`${translationNamespace}.parallax.leftExtraTexts.${index}`, text)}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="column column-inner column-2 tw-mt-[3em]">
                      <h4 className="h4-white">{t(`${translationNamespace}.parallax.rightTitle`, textContent.rightTitle)}</h4>
                      <p className="grey-text">{t(`${translationNamespace}.parallax.rightText`, textContent.rightText)}</p>
                      {textContent.rightExtraTexts && textContent.rightExtraTexts.length > 0 && (
                        <div className="extra-texts">
                          {textContent.rightExtraTexts.slice(0, 3).map((text, index) => (
                            <p key={index} className="grey-text extra-text">
                              {t(`${translationNamespace}.parallax.rightExtraTexts.${index}`, text)}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <img
                src={images.layer3}
                loading="eager"
                width="800"
                data-parallax-layer="4"
                alt=""
                className="parallax__layer-img"
              />
            </div>
            <div className="parallax__fade"></div>
          </div>
        </section>

        {contentBelowParallax && (
          <section className="parallax__content">
            <div className="parallax__content-container">
              {contentBelowParallax}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ParallaxComponent;